import "@blissary/design-tokens";
import { get, set, migrate } from "@blissary/settings";
import { BlissColorHelper } from "@blissary/utils";
import { BlissSVGBuilder } from "bliss-svg-builder";
import { css, html, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import "./movable-shape.js";
import "./shape-board.js";
import "./editable-board.js";

// WebAwesome theme and components
import "@awesome.me/webawesome/dist/styles/themes/default.css";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/slider/slider.js";
import "@awesome.me/webawesome/dist/components/switch/switch.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";
import "@awesome.me/webawesome/dist/components/radio/radio.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/tab-group/tab-group.js";
import "@awesome.me/webawesome/dist/components/tab/tab.js";
import "@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js";
import "@awesome.me/webawesome/dist/components/tag/tag.js";

// Shell components (navigation, animated tabs)
import "@blissary/shell-components";
import "@blissary/shell-components/tab-group";
import { clipboardIcon, closeIcon, homeIcon, bookOpenIcon, penToolIcon, downloadIcon, globeIcon, infoIcon, mailIcon } from "@blissary/shell-components/icons";

const appBase = window.location.origin + window.location.pathname; //url app is served from, even when served from a sub directory

class BlissMaker extends LitElement {
  static get properties() {
    return {
      fileFormat: {
        type: String,
      },
      exportGrid: {
        type: Boolean,
      },
      backgroundColor: {
        type: String,
      },
      strokeWidth: {
        type: Number,
      },
      gridSkyColor: {
        type: String,
      },
      gridEarthColor: {
        type: String,
      },
      gridMajorColor: {
        type: String,
      },
      gridMediumColor: {
        type: String,
      },
      gridMinorColor: {
        type: String,
      },
      urlQueryString: {
        type: String,
      },
      imageHeight: {
        type: Number,
      },
      imageWidth: {
        type: Number,
      },
      snapStep: {
        type: Number,
      },
      extendedDataIsFetched: {
        type: Boolean,
      },
    };
  }

  constructor() {
    super();
    migrate();

    this.code = "";
    this.fileFormat = get("maker.format");
    this.exportGrid = get("maker.grid");
    this.strokeWidth = get("maker.strokeWidth");
    this.imageHeight = get("maker.height");
    this.imageWidth = 9;
    this.snapStep = get("maker.snapStep");
    this._inlineOptions = {};
    this._debounceTimer = null;

    // Apply background from settings
    const bg = get("maker.background");
    this._applyBackgroundFromSetting(bg);
    this._backgroundSelectValue = bg.charAt(0).toUpperCase() + bg.slice(1);
    // Extract code from raw URL, treating # as literal (not fragment)
    const raw = window.location.href;
    const qIdx = raw.indexOf("?");
    const params = qIdx >= 0 ? new URLSearchParams(raw.slice(qIdx + 1)) : new URLSearchParams();
    this.urlQueryString = params.get("c") || "";
    this.tabSvgs = [
      this.getTabSvg("C8;HC4N:2,1.5;QC2SE:4,4.5;VQW2SW:1.75,4.75;HQW2SW:2.5,4;HW2S:3,3"),
      this.getTabSvg("S8;OR42N:2,5;OS2N:3,4;R42:2,1"),
      this.getTabSvg("VL4;HL4:4,8;DL4N:3,1;DL2-4N:1.75,0;DL4-2N:4,4.25"),
      this.getTabSvg("AT8N;RT4N:2,5;RT2NW;RT2NE:6,0"),
      this.getTabSvg("AA8N;RA4N:2,5;RA2NW;RA2NE:6,0"),
      this.getTabSvg("W8W;SC2:2,3;DC2:4,3;E:4,0"),
      this.getTabSvg("LARR4E:0,4;P4W:6,4;LARR4W:4,0;P4E"),
      this.getTabSvg("DOT:1,1;Xa:-0.5,-8;Xb:2.4,-10;Xc:5.2,-12;COMMA:7,7"),
      this.getTabSvg("HL8;DL8S;HL8:0,8"),
    ];

    // bliss-svg-builder includes all character data, no need to fetch from backend
    this.extendedDataIsFetched = true;

    this.updateComplete.then(() => {
      this.adaptToViewport();
      window.addEventListener("resize", () => {
        this.adaptToViewport();
      });
    });
  }

  _applyBackgroundFromSetting(background) {
    if (background === "transparent") {
      this.backgroundColor = "rgba(0,0,0,0)";
      this.gridSkyColor = "rgba(0,0,0,0.48)";
      this.gridEarthColor = "rgba(0,0,0,0.48)";
      this.gridMajorColor = "rgba(0,0,0,0.22)";
      this.gridMediumColor = "rgba(0,0,0,0.08)";
      this.gridMinorColor = "rgba(0,0,0,0.08)";
    } else {
      const colorName = background === "colored" ? "white" : background;
      const colors = BlissColorHelper.getColorValuesByColorName(colorName);
      this.backgroundColor = colors.background;
      this.gridSkyColor = colors.gridSkyColor;
      this.gridEarthColor = colors.gridEarthColor;
      this.gridMajorColor = colors.gridMajorColor;
      this.gridMediumColor = colors.gridMediumColor;
      this.gridMinorColor = colors.gridMinorColor;
    }
  }

  // Debounced write to settings
  _debouncedSet(name, value) {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => set(name, value), 300);
  }

  adaptToViewport() {
    if (this._rafPending) return;
    this._rafPending = true;
    requestAnimationFrame(() => {
      this._rafPending = false;
      const contentWidth = 700;
      const contentHeight = 991;
      const scale = Math.min(1, window.innerWidth / contentWidth, window.innerHeight / contentHeight);
      const wrapper = this.shadowRoot.getElementById("scaleWrapper");
      if (wrapper) {
        // min-height in local (unscaled) space so footer is pushed to viewport bottom
        const navHeight = this.shadowRoot.querySelector("blissary-navigation")?.getBoundingClientRect().height || 0;
        const localViewportHeight = (window.innerHeight - navHeight) / scale;
        wrapper.style.minHeight = `${localViewportHeight}px`;

        wrapper.style.width = `${contentWidth}px`;
        if (scale < 1) {
          wrapper.style.transform = `scale(${scale})`;
          wrapper.style.transformOrigin = "top left";
          // Center the visually scaled content
          const visualWidth = contentWidth * scale;
          wrapper.style.marginLeft = `${(window.innerWidth - visualWidth) / 2}px`;
          wrapper.style.marginRight = "0";
        } else {
          wrapper.style.transform = "";
          wrapper.style.transformOrigin = "top center";
          wrapper.style.marginLeft = "auto";
          wrapper.style.marginRight = "auto";
        }
      }
    });
  }

  async firstUpdated() {
    this.handleTabActivated({ detail: { name: "tab-0" } });
  }

  async fetchBlissPng(options) {
    const response = await fetch(`${appBase}api/bliss-png`, {
      method: "POST",
      headers: {
        Accept: "image/png",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw response;
    }

    return response;
  }
  async fetchBlissSvg(options) {
    const response = await fetch(`${appBase}api/bliss-svg`, {
      method: "POST",
      headers: {
        Accept: "image/svg+xml",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw response;
    }

    return response;
  }

  async writeToClipboard(image) {
    if (this.fileFormat === "svg") {
      const svgCode = image;
      await navigator.clipboard.writeText(svgCode);
    } else {
      //png
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": image,
        }),
      ]);
    }
  }

  // Build the export options object, merging inline options with UI values.
  // Inline options (from the input string) take precedence over UI controls.
  // Includes size-dependent overrides for stroke widths and grid rendering.
  _buildExportOptions(blissCode) {
    // Extract the raw global options string and the code body
    const prefixMatch = blissCode.match(/^\[([^\]]*)\]\|\|(.*)$/);
    const inlineOptionsStr = prefixMatch ? prefixMatch[1] : "";
    const codeOnly = prefixMatch ? prefixMatch[2] : blissCode;
    const io = this._inlineOptions;

    const options = {
      blisscode: this.fixBlissCode(codeOnly),
      inlineOptions: inlineOptionsStr,
      grid: "grid" in io ? io.grid !== "0" : this.exportGrid,
      height: "svg-height" in io ? parseInt(io["svg-height"], 10) : this.imageHeight,
      background: "background" in io ? io.background : this.backgroundColor,
      gridSkyColor: this.gridSkyColor,
      gridEarthColor: this.gridEarthColor,
      gridMajorColor: this.gridMajorColor,
      gridMediumColor: this.gridMediumColor,
      gridMinorColor: this.gridMinorColor,
      strokeWidth: "stroke-width" in io ? Number(io["stroke-width"]) : this.strokeWidth,
      resolution: get("maker.dpi"),
    };

    if (options.background === "rgba(0,0,0,0)") {
      options.background = "";
      delete options.gridSkyColor;
      delete options.gridEarthColor;
      delete options.gridMajorColor;
      delete options.gridMediumColor;
      delete options.gridMinorColor;
    }

    if (options.height < 300) {
      options.gridSkyColor = options.gridMajorColor;
      options.gridEarthColor = options.gridMajorColor;
    }

    if (options.height === 86 || options.height === 344) {
      options.strokeWidth = 0.5;
      options.margin = 0.75;
      options.gridStrokeWidth = 0.5;
      options.gridSkyStrokeWidth = 0.5;
      options.gridEarthStrokeWidth = 0.5;
      options.gridMajorStrokeWidth = 0.5;
      options.gridMediumStrokeWidth = 0.5;
      options.gridMinorStrokeWidth = 0;
    } else if (options.height === 43 || options.height === 172) {
      options.strokeWidth = 0.5;
      options.margin = 0.75;
      options.gridStrokeWidth = 0.5;
      options.gridSkyStrokeWidth = 0.5;
      options.gridEarthStrokeWidth = 0.5;
      options.gridMajorStrokeWidth = 0.5;
      options.gridMediumStrokeWidth = 0;
      options.gridMinorStrokeWidth = 0;
    } else if (options.height === 66 || options.height === 264) {
      options.strokeWidth = 0.666;
      options.margin = 1;
      options.gridStrokeWidth = 0.666;
      options.gridSkyStrokeWidth = 0.666;
      options.gridEarthStrokeWidth = 0.666;
      options.gridMajorStrokeWidth = 0.666;
      options.gridMediumStrokeWidth = 0.666;
      options.gridMinorStrokeWidth = 0;
    } else if (options.height === 33 || options.height === 132) {
      options.strokeWidth = 0.666;
      options.margin = 1;
      options.gridStrokeWidth = 0.666;
      options.gridSkyStrokeWidth = 0.666;
      options.gridEarthStrokeWidth = 0.666;
      options.gridMajorStrokeWidth = 0.666;
      options.gridMediumStrokeWidth = 0;
      options.gridMinorStrokeWidth = 0;
    } else {
      options.margin = 0.75;
      options.gridStrokeWidth = 0.166;
      options.gridSkyStrokeWidth = 0.166;
      options.gridEarthStrokeWidth = 0.166;
      options.gridMajorStrokeWidth = 0.166;
      options.gridMediumStrokeWidth = 0.166;
      options.gridMinorStrokeWidth = 0.166;
    }

    return options;
  }

  fixBlissCode(code) {
    let leftmostX;
    const chars = code.split("/");
    const char = chars[0];
    const shapes = char.split(";");
    for (const shape of shapes) {
      const [_shapeCode, coordinates] = shape.split(":");
      if (!coordinates) return code; // failed to parse, so return code without changes
      const [coordinateXStr, _coordinateYStr] = coordinates.split(",");
      const coordinateX = Number(coordinateXStr);

      if (Number.isNaN(coordinateX)) return code; // failed to parse, so return code without changes

      if (typeof leftmostX === "undefined") {
        leftmostX = coordinateX;
      } else {
        leftmostX = Math.min(leftmostX, coordinateX);
      }
    }
    if (leftmostX > 0) {
      for (let i = 0; i < shapes.length; i++) {
        let [shapeCode, coordinates] = shapes[i].split(":");
        const [coordinateXStr, coordinateYStr] = coordinates.split(",");
        let coordinateX = Number(coordinateXStr);
        coordinateX -= leftmostX;
        coordinates = [coordinateX, coordinateYStr].join(",");
        shapes[i] = [shapeCode, coordinates].join(":");
      }
    }
    chars[0] = shapes.join(";");
    code = chars.join("/");
    return code;
  }

  showSnackbar(message) {
    const snackbar = this.shadowRoot.getElementById("snackbar");
    const msgEl = snackbar.querySelector("#snackbarMessage");
    msgEl.textContent = message;
    snackbar.style.display = "flex";
    snackbar.removeAttribute("hidden");
    // Auto-hide after 4 seconds
    clearTimeout(this._snackbarTimeout);
    this._snackbarTimeout = setTimeout(() => {
      this.hideSnackbar();
    }, 4000);
  }

  hideSnackbar() {
    const snackbar = this.shadowRoot.getElementById("snackbar");
    snackbar.style.display = "none";
    snackbar.setAttribute("hidden", "");
  }

  async handleClickedDownload(_e) {
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    const blissCode = editableBoard.getBlissCode();

    if (blissCode === "") {
      this.showSnackbar(`The grid is empty! Drag shapes to the grid.`);
      return;
    }

    const downloadButton = this.shadowRoot.getElementById("download");
    downloadButton.disabled = true;
    const downloadSpinner = this.shadowRoot.getElementById("spinnerDownload");
    downloadSpinner.style.display = "inline-block";

    const options = this._buildExportOptions(blissCode);

    if (this.fileFormat === "png" && options.blisscode.includes("[style=")) {
      this.showSnackbar(
        "Note: CSS styles (e.g. drop-shadow) are not supported in PNG export. Use SVG for full CSS support.",
      );
    }

    const link = document.createElement("a");
    let response;

    try {
      if (this.fileFormat === "png") {
        response = await this.fetchBlissPng(options);
      } else if (this.fileFormat === "svg") {
        response = await this.fetchBlissSvg(options);
      } else {
        throw new Error("invalid fileFormat");
      }
    } catch (e) {
      this.showSnackbar(`failed to generate image! 🙁 (server response: ${e.status} - ${e.statusText})`);
      downloadSpinner.style.display = "none";
      downloadButton.disabled = false;
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `bliss-maker-export.${this.fileFormat}`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    link.remove();
    downloadSpinner.style.display = "none";
    downloadButton.disabled = false;
  }

  async handleClickedToClipboard(_e) {
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    const blissCode = editableBoard.getBlissCode();

    if (blissCode === "") {
      this.showSnackbar(`The grid is empty! Drag shapes to the grid.`);
      return;
    }

    const clipboardButton = this.shadowRoot.getElementById("clipboard");
    clipboardButton.disabled = true;
    const clipboardSpinner = this.shadowRoot.getElementById("spinnerClipboard");
    clipboardSpinner.style.display = "inline-block";

    const options = this._buildExportOptions(blissCode);

    if (this.fileFormat === "png" && blissCode.includes("[style=")) {
      this.showSnackbar(
        "Note: CSS styles (e.g. drop-shadow) are not supported in PNG export. Use SVG for full CSS support.",
      );
    }

    try {
      if (this.fileFormat === "png") {
        const response = await this.fetchBlissPng(options);
        const blob = await response.blob();
        await this.writeToClipboard(blob);
      } else if (this.fileFormat === "svg") {
        const response = await this.fetchBlissSvg(options);
        const text = await response.text();
        await this.writeToClipboard(text);
      } else {
        console.error("incorrect file format");
      }
    } catch (error) {
      this.showSnackbar(`failed to copy image to clipboard! 🙁 (error: ${error})`);
    }

    clipboardSpinner.style.display = "none";
    clipboardButton.disabled = false;
  }

  // Get the scale factor from the scaleWrapper transform
  _getScale() {
    const wrapper = this.shadowRoot.getElementById("scaleWrapper");
    if (!wrapper) return 1;
    return wrapper.getBoundingClientRect().width / wrapper.offsetWidth || 1;
  }

  handleShapeDropped(e) {
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    if (!editableBoard || editableBoard.isComposition) return;
    const rect = editableBoard.getBoundingClientRect();
    if (
      e.detail.x > rect.x &&
      e.detail.x < rect.x + rect.width &&
      e.detail.y > rect.y &&
      e.detail.y < rect.y + rect.height
    ) {
      editableBoard.addShape(e.detail.code, e.detail.x, e.detail.y, this.snapStep);
    }
    editableBoard.hideTempShape();
  }

  handleShapeMoved(e) {
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    if (!editableBoard || editableBoard.isComposition) return;
    const rect = editableBoard.getBoundingClientRect();
    if (
      e.detail.x + e.detail.width > rect.x &&
      e.detail.x < rect.x + rect.width &&
      e.detail.y + e.detail.height > rect.y &&
      e.detail.y < rect.y + rect.height
    ) {
      editableBoard.showTempShape(e.detail.code, e.detail.x, e.detail.y, this.snapStep);
    } else {
      editableBoard.hideTempShape();
    }
  }

  handleStrokeWidthSliderInput(e) {
    this.strokeWidth = Number(e.target.value).toFixed(2);
    this._debouncedSet("maker.strokeWidth", Number(this.strokeWidth));
  }

  handleExportGridChanged(e) {
    this.exportGrid = e.target.checked;
    set("maker.grid", this.exportGrid);
  }

  handleImageSizeSliderChanged(e) {
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    const dimensions = editableBoard.dimensions;
    const value = Number(e.target.value);

    const resolutionMultiplier = 4; //for now... 1=lo res 96ppi, 4=hi res 384ppi
    switch (value) {
      case 1:
        this.imageHeight = 43 * resolutionMultiplier; //40+3
        this.imageWidth = (dimensions.width * 2 + 3) * resolutionMultiplier;
        break;
      case 2:
        this.imageHeight = 66 * resolutionMultiplier; //60+6
        this.imageWidth = (dimensions.width * 3 + 6) * resolutionMultiplier;
        break;
      case 3:
        this.imageHeight = 86 * resolutionMultiplier; //80+6
        this.imageWidth = (dimensions.width * 4 + 6) * resolutionMultiplier;
        break;
      case 4:
        this.imageHeight = 129 * resolutionMultiplier; //120+9
        this.imageWidth = (dimensions.width * 6 + 9) * resolutionMultiplier;
        break;
      case 5:
        this.imageHeight = 258 * resolutionMultiplier; //240+18
        this.imageWidth = (dimensions.width * 12 + 18) * resolutionMultiplier;
        break;
      case 6:
        this.imageHeight = 516 * resolutionMultiplier; //480+36
        this.imageWidth = (dimensions.width * 24 + 36) * resolutionMultiplier;
        break;
      default:
        break;
    }
    this._debouncedSet("maker.height", this.imageHeight);
  }

  handleSnapStepSliderChanged(e) {
    this.snapStep = Number(e.target.value) / 2;
    set("maker.snapStep", this.snapStep);
  }

  handleUrlQueryStringInput(e) {
    this.urlQueryString = e.target.value;
    // Live-update the board on every keystroke
    const editableBoard = this.shadowRoot.getElementById("editableBoard");
    editableBoard.setBlissCode(this.urlQueryString);
  }

  handleInputStateChanged(e) {
    const { valid, inlineOptions } = e.detail;
    this._inlineOptions = valid ? inlineOptions : {};

    // Input field validation styling
    const input = this.shadowRoot.getElementById("urlQueryStringInput");
    if (input) {
      if (valid) {
        input.removeAttribute("data-user-invalid");
        input.helpText = "";
      } else {
        input.setAttribute("data-user-invalid", "");
        input.helpText = "Invalid Bliss SVG Builder string";
      }
    }

    // Disable UI controls that have inline option overrides
    // Map from inline option keys to UI control element IDs
    const optionToControl = {
      "stroke-width": "strokeWidthSlider",
      background: "backgroundColorSelect",
      grid: "exportGridSwitch",
      "svg-height": "imageSizeSlider",
    };

    for (const [optKey, controlId] of Object.entries(optionToControl)) {
      const control = this.shadowRoot.getElementById(controlId);
      if (control) {
        control.disabled = valid && optKey in inlineOptions;
      }
    }
  }

  handleBackgroundColorSelected(e) {
    const value = e.target.value;
    if (!value) return;

    if (value === "Transparent") {
      this.backgroundColor = "rgba(0,0,0,0)";
      this.gridSkyColor = "rgba(0,0,0,0.48)";
      this.gridEarthColor = "rgba(0,0,0,0.48)";
      this.gridMajorColor = "rgba(0,0,0,0.22)";
      this.gridMediumColor = "rgba(0,0,0,0.08)";
      this.gridMinorColor = "rgba(0,0,0,0.08)";
      set("maker.background", "transparent");
    } else {
      const colorName = value.toLowerCase();
      const colors = BlissColorHelper.getColorValuesByColorName(colorName);
      this.backgroundColor = colors.background;
      this.gridSkyColor = colors.gridSkyColor;
      this.gridEarthColor = colors.gridEarthColor;
      this.gridMajorColor = colors.gridMajorColor;
      this.gridMediumColor = colors.gridMediumColor;
      this.gridMinorColor = colors.gridMinorColor;
      set("maker.background", value.toLowerCase());
    }
  }

  handleFileFormatChanged(e) {
    this.fileFormat = e.target.value;
    set("maker.format", this.fileFormat);
  }

  handleUrlChanged(e) {
    // Skip update while the input field is focused — the user is typing,
    // and re-setting urlQueryString would reset the cursor position.
    const input = this.shadowRoot.getElementById("urlQueryStringInput");
    if (input && input.matches(":focus-within")) return;

    const urlParams = new URLSearchParams(e.detail.url);
    this.urlQueryString = urlParams.get("c") || "";
  }

  handleDimensionsChanged(e) {
    const dimensions = e.detail.dimensions;
    const imageSizeSlider = this.shadowRoot.getElementById("imageSizeSlider");
    const value = Number(imageSizeSlider.value);

    const resolutionMultiplier = 4; //for now... 1=lo res 96ppi, 4=hi res 384ppi
    switch (value) {
      case 1:
        this.imageHeight = 43 * resolutionMultiplier; //40+3
        this.imageWidth = (dimensions.width * 2 + 3) * resolutionMultiplier;
        break;
      case 2:
        this.imageHeight = 66 * resolutionMultiplier; //60+6
        this.imageWidth = (dimensions.width * 3 + 6) * resolutionMultiplier;
        break;
      case 3:
        this.imageHeight = 86 * resolutionMultiplier; //80+6
        this.imageWidth = (dimensions.width * 4 + 6) * resolutionMultiplier;
        break;
      case 4:
        this.imageHeight = 129 * resolutionMultiplier; //120+9
        this.imageWidth = (dimensions.width * 6 + 9) * resolutionMultiplier;
        break;
      case 5:
        this.imageHeight = 258 * resolutionMultiplier; //240+18
        this.imageWidth = (dimensions.width * 12 + 18) * resolutionMultiplier;
        break;
      case 6:
        this.imageHeight = 516 * resolutionMultiplier; //480+36
        this.imageWidth = (dimensions.width * 24 + 36) * resolutionMultiplier;
        break;
      default:
        break;
    }
  }

  handleTabActivated(e) {
    // wa-tab-show event detail has { name } which is the panel name
    const panelName = e.detail.name;
    const index = parseInt(panelName.replace("tab-", ""), 10);
    switch (index) {
      case 0:
        this.code =
          "C8:1,1;C4:3,3;C2:4,4;QC1NW:14,14;QC2NW:13,13;QC4NW:11,11;QC1NE:17,14;QC2NE:17,13;QC4NE:17,11;QC1SW:14,17;QC2SW:13,17;QC4SW:11,17;QC1SE:17,17;QC2SE:17,17;QC4SE:17,17;HC2N:4,14;HC4N:3,13;HC8N:1,11;HC2S:4,17;HC4S:3,17;HC8S:1,17;HC2W:14,4;HC4W:13,3;HC8W:11,1;HC2E:17,4;HC4E:17,3;HC8E:17,1;HW2N:26.5,14.5;HW4N:25.5,13.5;HW8N:23.5,11.5;HW2S:26.5,17;HW4S:25.5,17.5;HW8S:23.5,18.5;HW2W:26,4;HW4W:25,3;HW8W:23,1;HW2E:28.5,4;HW4E:29,3;HW8E:30,1;OC2SE:37,4.5;OC4SE:36,3.5;OC8SE:34,1.5;OC2SW:47,4.5;OC4SW:46,3.5;OC8SW:44,1.5;OC2NE:37,14.5;OC4NE:36,13.5;OC8NE:34,11.5;OC2NW:47,14.5;OC4NW:46,13.5;OC8NW:44,11.5";
        break;
      case 1:
        this.code =
          "S2:4,4;S4:3,3;S8:1,1;OR24E:13,3;OR48E:11,1;OR24W:17,3;OR48W:17,1;OR42N:3,17;OR84N:1,17;OR42S:3,13;OR84S:1,11;OS2E:26,4;OS4E:25,3;OS8E:23,1;OS2W:36,4;OS4W:35,3;OS8W:33,1;OR42E:25,12;OR84E:23,11;OR42W:35,12;OR84W:33,11;R48:14,13;R24:15,15;R84:28,17;R42:30,18;OS2S:46,4;OS4S:45,3;OS8S:43,1;OS2N:46,14;OS4N:45,13;OS8N:43,11;OR24S:54,3;OR48S:53,1;OR24N:54,13;OR48N:53,11";
        break;
      case 2:
        this.code =
          "VL8:1,1;VL4:3,1;VL2:5,1;VL1:7,1;VL.5:9,1;VL3:3,6;VL1.5:5,7.5;HL8:13,1;HL4:17,3;HL2:19,5;HL1:20,7;HL.5:20.5,9;HL3:13,3;HL1.5:13,5;DL8N:1,10;DL4N:6,11;DL3N:2,16;DL2N:8,13;DL1.5N:4,17.5;DL1N:9,15;DL.5N:9.5,17;DL8S:13,10;DL4S:12,11;DL3S:17,16;DL2S:12,13;DL1.5S:16.5,17.5;DL1S:12,15;DL.5S:12,17;DL4-1S:25,17;DL2-.5S:27,16;DL8-4S:21,11;DL4-2S:25,11;DL2-1S:27,10;DL1-.5S:28,8.5;DL3-2S:26,5;DL1.5-1S:27.5,4;DL4-1N:31,17;DL2-.5N:31,16;DL8-4N:31,11;DL4-2N:31,11;DL2-1N:31,10;DL1-.5N:31,8.5;DL3-2N:31,5;DL1.5-1N:31,4;DL1-4N:41,6;DL.5-2N:43,8;DL4-8N:45,2;DL2-4N:47,6;DL1-2N:49,8;DL.5-1N:51,9;DL2-3N:53,7;DL1-1.5N:55,9;DL1-4S:41,12;DL.5-2S:43,12;DL4-8S:45,12;DL2-4S:47,12;DL1-2S:49,12;DL.5-1S:51,12;DL2-3S:53,12;DL1-1.5S:55,12";
        break;
      case 3:
        this.code =
          "RT2NW:2.8,2.8;RT4NW:2.25,2.25;RT8NW:1,1;RT2NE:17.2,2.8;RT4NE:15.75,2.25;RT8NE:13,1;RT2SW:2.8,15.2;RT4SW:2.25,13.75;RT8SW:1,11;RT2SE:17.2,15.2;RT4SE:15.75,13.75;RT8SE:13,11;RT2W:3.85,9;RT4W:12,8;RT8W:2,6;RT2N:10,2.85;RT4N:9,11;RT8N:7,1;RT2E:17.15,9;RT4E:8,8;RT8E:16,6;RT2S:10,16.15;RT4S:9,7;RT8S:7,15;AT2S:32,2.875;AT4S:31,2.25;AT8S:29,1;AT2E:25.875,9;AT4E:25.25,8;AT8E:24,6;AT2W:38.125,9;AT4W:36.75,8;AT8W:34,6;AT2N:32,15.125;AT4N:31,13.75;AT8N:29,11";
        break;
      case 4:
        this.code =
          "RA2NW:3,3;RA4NW:2.25,2.25;RA8NW:1,1;RA2NE:17,3;RA4NE:15.75,2.25;RA8NE:13,1;RA2SW:3,15;RA4SW:2.25,13.75;RA8SW:1,11;RA2SE:17,15;RA4SE:15.75,13.75;RA8SE:13,11;RA2W:3.85,9;RA4W:12,8;RA8W:2,6;RA2N:10,2.85;RA4N:9,11;RA8N:7,1;RA2E:17.15,9;RA4E:8,8;RA8E:16,6;RA2S:10,16.15;RA4S:9,7;RA8S:7,15;AA2S:32,2.875;AA4S:31,2.25;AA8S:29,1;AA2E:25.875,9;AA4E:25.25,8;AA8E:24,6;AA2W:38.125,9;AA4W:36.75,8;AA8W:34,6;AA2N:32,15.125;AA4N:31,13.75;AA8N:29,11";
        break;
      case 5:
        this.code =
          "H:1,1;E:11,1;W8E:16.5,1;W8W:20.5,1;F:24,1;W8S:30,1;W8N:30,5;DC8:1,11;DC4:12,11;DC2:20,11;SC2:13,17;SC4:19,15;SC8:25,11;DLIC8N:36.5,10.5;DLIC8S:36.5,10.5;DLIC4N:40.5,2.5;DLIC4S:40.5,2.5;DLOC8NW:36,10;DLOC8NE:37,10;DLOC8SW:36,11;DLOC8SE:37,11;DLOC4NW:40,2;DLOC4NE:41,2;DLOC4SW:40,3;DLOC4SE:41,3";
        break;
      case 6:
        this.code =
          "LARR4N:9,4;LARR4W:4,9;LARR4E:14,9;LARR4S:9,14;P4N:9,1;P4W:1,9;P4E:19,9;P4S:9,19;LARR4NW:4,4;P3NW:1,1;LARR4NE:14,4;P3NE:18,1;LARR4SE:14,14;P3SE:18,18;LARR4SW:4,14;P3SW:1,18;LARR8N:33,1;LARR8W:25,9;LARR8E:37,9;LARR8S:33,13;LARR8NW:25,1;LARR8NE:37,1;LARR8SW:25,13;LARR8SE:37,13";
        break;
      case 7:
        this.code =
          "DOT:4,2.75;COMMA:8,2.75;Xa:0.7,-6.3;Xb:3.9,-6.3;Xc:7.1,-6.3;Xd:10,-6.3;Xe:13.1,-6.3;Xf:16.4,-6.3;Xg:18,-6.3;Xh:21.2,-6.3;Xi:24.4,-6.3;Xj:25.6,-6.3;Xk:26.8,-6.3;Xl:29.7,-6.3;Xm:30.9,-6.3;Xn:35.6,-6.3;Xo:38.7,-6.3;Xp:41.8,-6.3;Xq:45,-6.3;Xr:48.2,-6.3;Xs:50.2,-6.3;Xt:53.2,-6.3;Xu:54.8,-6.3;Xv:0.7,-1;Xw:3.7,-1;Xx:7.8,-1;Xy:10.6,-1;Xz:13.3,-1;XA:16,-1;XB:19.6,-1;XC:23.2,-1;XD:27.1,-1;XE:31,-1;XF:34.8,-1;XG:38,-1;XH:42.4,-1;XI:46.5,-1;XJ:48.1,-1;XK:50.9,-1;XL:54.5,-1;XM:0.7,5;XN:5.5,5;XO:9.7,5;XP:14.2,5;XQ:17.9,5;XR:22.3,5;XS:26.4,5;XT:30.1,5;XU:33.7,5;XV:36.9,5;XW:40.7,5;XX:46.1,5;XY:49.8,5;XZ:53.6,5";
        break;
      default:
        this.code = "";
    }
    this.requestUpdate();
  }

  getTabSvg(code) {
    const svg = new BlissSVGBuilder(`[grid=0;stroke-width=0.5;color=#6D3822;freestyle=1]||${code}`).svgElement;
    svg.setAttribute("width", 68);
    svg.setAttribute("height", 68);
    let viewBox = svg.getAttribute("viewBox");
    const viewBoxValues = viewBox.split(" ");
    viewBoxValues[0] = "-1";
    viewBoxValues[1] = "-1";
    viewBoxValues[2] = "10";
    viewBoxValues[3] = "10";
    viewBox = viewBoxValues.join(" ");
    svg.setAttribute("viewBox", viewBox);

    return svg;
  }

  static get styles() {
    return css`
      :host {
        display: block;
        user-select: none;
        background: var(--bl-color-background);
      }

      #scaleWrapper {
        width: 700px;
        margin: 0 auto;
        transform-origin: top center;
        display: flex;
        flex-direction: column;
      }

      #title {
        font-family: Artifika, Roboto, sans-serif;
        font-size: 30px;
        font-style: normal;
        font-variant: normal;
        font-weight: 700;
        padding: 5px;
      }

      editable-board {
        position: absolute;
        top: 0;
        left: 4px;
      }

      #actionBar {
        position: absolute;
        width: 700px;
        top: 267px;
        left: 4px;
        padding: 8px 0;
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      #exportOptions {
        position: absolute;
        width: 700px;
        top: 326px;
        left: 4px;
        padding: var(--bl-space-4, 16px);
        border: 1px solid var(--bl-color-border);
        border-radius: var(--bl-radius-md);
        box-sizing: border-box;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: auto auto auto;
        align-items: center;
        gap: 4px 16px;
      }

      #backgroundColorField {
        grid-column: 1;
        grid-row: 1 / 3;
      }

      #strokeWidthField {
        grid-column: 2;
        grid-row: 1;
      }

      #snapToGridField {
        grid-column: 3;
        grid-row: 1;
      }

      #exportGridField {
        grid-column: 1;
        grid-row: 3;
        margin-bottom: -4px;
      }

      #exportFileFormatField {
        grid-column: 2;
        grid-row: 2 / 4;
        margin-top: 10px;
      }

      #exportImageSizeField {
        grid-column: 3;
        grid-row: 2 / 4;
      }

      wa-tag {
        vertical-align: middle;
        margin-left: 4px;
      }

      wa-spinner {
        --indicator-color: var(--bl-color-primary-300);
        --track-color: var(--bl-color-primary);
        width: 28px;
        height: 28px;
        top: 4px;
        position: absolute;
        z-index: 10;
        display: none;
      }

      #spinnerClipboard {
        left: 9px;
      }

      #spinnerDownload {
        left: 22px;
      }

      #urlQueryStringDiv {
        position: absolute;
        top: 836px;
        left: 4px;
        width: 700px;
        padding: 8px 0;
        box-sizing: border-box;
        display: flex;
        gap: 8px;
        align-items: flex-end;
      }

      #urlQueryStringInput {
        flex: 1;
      }

      #urlQueryStringInput::part(form-control-label) {
        position: relative;
        z-index: 1;
        width: fit-content;
        margin-bottom: calc(-0.65em + 2px);
        margin-left: 8px;
        padding: 0 4px;
        background: var(--bl-color-background);
        font-family: var(--bl-font-body);
        font-size: var(--bl-text-sm);
        transition: color 150ms ease;
      }

      #urlQueryStringInput:focus-within::part(form-control-label) {
        color: var(--bl-color-accent, #b89530);
      }

      #urlQueryStringInput:focus-within::part(base) {
        outline-color: var(--bl-color-accent, #b89530);
        border-color: var(--bl-color-accent, #b89530);
      }

      #urlQueryStringInput[data-user-invalid]::part(base) {
        border-color: var(--wa-color-danger-600, #dc3545);
      }

      #urlQueryStringInput[data-user-invalid]:focus-within::part(base) {
        outline-color: var(--wa-color-danger-600, #dc3545);
        border-color: var(--wa-color-danger-600, #dc3545);
      }

      blissary-footer {
        width: 700px;
        margin-top: auto;
      }

      /* Snackbar toast */
      #snackbar {
        position: fixed;
        bottom: 40px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--bl-color-surface-overlay);
        color: var(--bl-color-text-on-overlay);
        padding: 12px 24px;
        border-radius: 4px;
        font-family: var(--bl-font-body);
        font-size: var(--bl-text-sm);
        z-index: 1000;
        display: none;
        align-items: center;
        gap: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        max-width: 600px;
      }

      wa-button svg {
        width: 1em;
        height: 1em;
        vertical-align: middle;
      }

      #snackbar wa-button {
        color: var(--bl-color-text-on-overlay);
      }
    `;
  }

  render() {
    return html`
      <style>
        #mainArea {
          width: 700px;
          min-height: 907px;
          margin: 0 auto;
          position: relative;
          view-transition-name: page-content;
          scrollbar-width: none;
        }

        #mainArea::-webkit-scrollbar {
          display: none;
        }

        #shapeGroup {
          position: absolute;
          left: 4px;
          top: 480px;
          width: 700px;
          box-sizing: border-box;
          border: 1px solid var(--bl-color-border);
          border-radius: var(--bl-radius-md);
        }

        #tabBar {
          background: var(--bl-color-primary-100);
          padding-bottom: 2px;
          border-radius: var(--bl-radius-md) var(--bl-radius-md) 0 0;
          overflow: hidden;
        }

        wa-tab-group#tabGroup {
          --track-color: transparent;
        }

        wa-tab-group#tabGroup::part(tabs) {
          display: grid;
          grid-template-columns: repeat(9, 1fr);
          background: var(--bl-color-primary-100);
          padding: 2px 2px 0 2px;
          gap: 2px;
        }

        wa-tab-group#tabGroup::part(body) {
          display: none;
        }

        wa-tab {
          padding: 0;
        }

        wa-tab::part(base) {
          padding: 2px;
          justify-content: center;
        }

        wa-tab svg {
          width: 68px;
          height: 68px;
          border: 1px solid var(--bl-color-primary);
          border-radius: 6px;
        }

        .form-field {
          font-family: var(--bl-font-body);
          font-size: var(--bl-text-xs);
          color: var(--bl-color-text-muted);
        }

        .form-field > label {
          display: flex;
          align-items: center;
          min-height: 24px;
          margin-bottom: 4px;
        }

        .form-field > label wa-tag {
          margin-left: auto;
        }

        wa-radio-group {
          font-size: var(--bl-text-base);
        }

        wa-select::part(combobox),
        wa-option::part(base) {
          font-family: var(--bl-font-body);
          color: var(--bl-color-text-primary);
        }

        wa-radio::part(label) {
          font-family: var(--bl-font-body);
          color: var(--bl-color-text-primary);
        }

        wa-button::part(label) {
          font-family: var(--bl-font-body);
        }

        #exportGridField {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        #exportGridField wa-switch {
          margin-left: auto;
        }

        wa-slider {
          width: 100%;
        }

        wa-slider::part(track) {
          background: var(--bl-color-primary-100);
        }

        wa-slider::part(indicator) {
          background-color: var(--bl-color-primary-200);
        }

        wa-select {
          width: 100%;
        }
      </style>
      <blissary-navigation
        title="Bliss Maker"
        current-path="/maker/"
        .items=${[
          { label: "Home", href: "/", icon: homeIcon },
          { label: "Bliss Dictionary", href: "/dictionary/", icon: bookOpenIcon },
          { label: "Bliss Maker", href: "/maker/", icon: penToolIcon, disabled: true },
          { label: "Assets", href: "/assets", icon: downloadIcon },
          { label: "Resources", href: "/resources", icon: globeIcon },
          { label: "About", href: "/about", icon: infoIcon },
          { label: "Contact", href: "/contact", icon: mailIcon },
        ]}>
      </blissary-navigation>
      <div id="scaleWrapper">
      <div id="mainArea">
        ${
          this.extendedDataIsFetched
            ? html`<editable-board
                @dimensions-changed="${this.handleDimensionsChanged}"
                @url-changed="${this.handleUrlChanged}"
                @input-state-changed="${this.handleInputStateChanged}"
                @shape-moved="${this.handleShapeMoved}"
                @shape-dropped="${this.handleShapeDropped}"
                strokeWidth="${this.strokeWidth}"
                .backgroundColor="${this.backgroundColor === "rgba(0,0,0,0)" ? "" : this.backgroundColor}"
                .gridSkyColor="${this.gridSkyColor}"
                .gridEarthColor="${this.gridEarthColor}"
                .gridMajorColor="${this.gridMajorColor}"
                .gridMediumColor="${this.gridMediumColor}"
                .gridMinorColor="${this.gridMinorColor}"
                .snapStep="${this.snapStep}"
                id="editableBoard">
              </editable-board>`
            : ""
        }
        <div id="actionBar">
          <wa-button id="clipboard" @click="${this.handleClickedToClipboard}" appearance="filled" variant="brand">
            <span slot="start">${unsafeHTML(clipboardIcon)}</span>
            To clipboard
            <wa-spinner id="spinnerClipboard"></wa-spinner>
          </wa-button>
          <wa-button id="download" @click="${this.handleClickedDownload}" appearance="filled" variant="brand">
            <span slot="start">${unsafeHTML(downloadIcon)}</span>
            Download
            <wa-spinner id="spinnerDownload"></wa-spinner>
          </wa-button>
        </div>
        <div id="exportOptions">
          <div class="form-field" id="backgroundColorField">
            <label>Background color</label>
            <wa-select id="backgroundColorSelect" value="${this._backgroundSelectValue}" @change="${this.handleBackgroundColorSelected}">
              <wa-option value="Transparent">Transparent</wa-option>
              <wa-option value="White">White</wa-option>
              <wa-option value="Yellow">Yellow</wa-option>
              <wa-option value="Green">Green</wa-option>
              <wa-option value="Red">Red</wa-option>
              <wa-option value="Blue">Blue</wa-option>
              <wa-option value="Gray">Gray</wa-option>
              <wa-option value="Orange">Orange</wa-option>
            </wa-select>
          </div>
          <div class="form-field" id="strokeWidthField">
            <label>Stroke width <wa-tag size="small" pill variant="brand">${this.strokeWidth} sq</wa-tag></label>
            <wa-slider id="strokeWidthSlider" @input="${this.handleStrokeWidthSliderInput}"
              min="0.1" max="1" value="0.5" step="0.01"></wa-slider>
          </div>
          <div class="form-field" id="snapToGridField">
            <label>Snap to grid <wa-tag size="small" pill variant="brand">${this.snapStep ? `${this.snapStep} sq` : "none"}</wa-tag></label>
            <wa-slider @input="${this.handleSnapStepSliderChanged}"
              min="0" max="2" value="2" step="1"></wa-slider>
          </div>
          <div class="form-field" id="exportGridField">
            <label>Export grid</label>
            <wa-switch id="exportGridSwitch" @change="${this.handleExportGridChanged}" checked></wa-switch>
          </div>
          <div class="form-field" id="exportFileFormatField">
            <label>Export file format</label>
            <wa-radio-group orientation="horizontal" name="fileFormat" value="png" @change="${this.handleFileFormatChanged}">
              <wa-radio value="png">PNG</wa-radio>
              <wa-radio value="svg">SVG</wa-radio>
            </wa-radio-group>
          </div>
          <div class="form-field" id="exportImageSizeField">
            <label>Export image size <wa-tag size="small" pill variant="brand">${this.imageWidth}x${this.imageHeight} px</wa-tag></label>
            <wa-slider id="imageSizeSlider" @input="${this.handleImageSizeSliderChanged}"
              min="1" max="6" value="4" step="1"></wa-slider>
          </div>
        </div>
        <div id="shapeGroup">
          <div id="tabBar">
            <bl-tab-group>
              <wa-tab-group id="tabGroup" without-scroll-controls @wa-tab-show="${this.handleTabActivated}">
                ${[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => html`<wa-tab slot="nav" panel="tab-${i}">${this.tabSvgs[i]}</wa-tab>`)}
                ${[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => html`<wa-tab-panel name="tab-${i}"></wa-tab-panel>`)}
              </wa-tab-group>
            </bl-tab-group>
          </div>
          <shape-board
            @shape-moved="${this.handleShapeMoved}"
            @shape-dropped="${this.handleShapeDropped}"
            code="${this.code}">
          </shape-board>
        </div>
        <div id="urlQueryStringDiv">
          <wa-input
            id="urlQueryStringInput"
            label="Bliss SVG Builder input string"
            autocomplete="off"
            .value="${this.urlQueryString}"
            @input="${this.handleUrlQueryStringInput}">
          </wa-input>
        </div>
      </div>
      <blissary-footer></blissary-footer>
      </div>
      <div id="snackbar" hidden>
        <span id="snackbarMessage"></span>
        <wa-button appearance="plain" size="small" @click="${this.hideSnackbar}">
          ${unsafeHTML(closeIcon)}
        </wa-button>
      </div>
    `;
  }
}

customElements.define("bliss-maker", BlissMaker);
